import { z } from 'zod';
import { ConsentDecision } from './legal.js';
import { Id } from './primitives.js';
import { Permission, RoleCode } from './rbac.js';
/**
 * FR-01: the employee number — Nomor Induk Karyawan — and the ONLY login
 * identifier.
 *
 * This replaced a union of email and phone. Both clients used to decide which
 * one had been typed by looking for an "@", which is a guess, and the guess was
 * being made about the wrong thing entirely: the field team are employees, the
 * company issues each of them a NIK, and a salesperson is far more likely to
 * know that number than to have a work email at all.
 *
 * `.trim().toUpperCase()` runs BEFORE the regex, so the transform is the
 * normaliser and the parsed value is already canonical — every consumer, server
 * or client, gets the same folded string without anybody remembering to call a
 * helper. That is what lets the login predicate be a plain equality match on a
 * unique index instead of a `lower()` expression, and what stops `emp001` and
 * `EMP001` becoming two accounts.
 */
export const Nik = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{3,20}$/, 'NIK harus 3–20 huruf kapital atau angka');
/**
 * Kept as an object rather than a bare string so the login body stays
 * `{ identifier: … }` and the shape of the request is unchanged for anything
 * that logs it or matches on it.
 */
export const LoginIdentifier = z.object({ nik: Nik });
/**
 * Which kind of client is asking, and therefore how its refresh token travels.
 *
 * THIS IS SET AT LOGIN AND NOWHERE ELSE. The server stores it on the token
 * family and every later refresh obeys the stored value, ignoring whatever the
 * request claims to be. That is not indirection for its own sake — see the note
 * on `RefreshRequest` for the attack it closes.
 */
export const ClientType = z.enum(['WEB', 'NATIVE']);
/**
 * The handset a native session belongs to.
 *
 * Native sessions are bound to a device so a token lifted off one phone cannot
 * be replayed from another, and so a lost handset can be revoked on its own
 * rather than by ending every session the user has. `deviceId` is generated by
 * the app on first launch and kept in the platform keystore — it is first-party
 * and is NOT an advertising identifier or a hardware serial.
 */
export const DeviceInfo = z.object({
    deviceId: Id,
    platform: z.enum(['IOS', 'ANDROID']),
    appVersion: z.string().max(32),
    /** For the session list an administrator reads; never used as a check. */
    model: z.string().max(80).optional(),
});
export const LoginRequest = z
    .object({
    identifier: LoginIdentifier,
    password: z.string().min(6).max(256),
    /** Present only when the account has TOTP enrolled (FR-01, optional 2FA). */
    totpCode: z.string().regex(/^[0-9]{6}$/).optional(),
    /**
     * Defaulted to WEB so every existing browser client is unchanged: it sends
     * nothing, gets a cookie, and behaves exactly as it did before native
     * existed.
     */
    client: ClientType.default('WEB'),
    device: DeviceInfo.optional(),
})
    .refine((value) => value.client !== 'NATIVE' || value.device !== undefined, {
    message: 'aplikasi harus menyertakan identitas perangkat',
    path: ['device'],
});
/**
 * `POST /auth/refresh`.
 *
 * A WEB session's refresh token is an HttpOnly cookie and never appears here;
 * this body exists for native clients, which have no cookie jar worth trusting
 * and carry the token in the platform keystore instead.
 *
 * THE SERVER DOES NOT CHOOSE BETWEEN THEM BASED ON THIS REQUEST. The delivery
 * mode is a property of the token family, fixed at login, and a token issued to
 * a cookie session is refused when presented in this body — and vice versa.
 * Anything softer is a privilege escalation: cookies are attached
 * automatically, so an endpoint that reads the cookie and writes the token into
 * a response body turns any XSS on a browser client into a full session
 * exfiltration. Setting the mode requires the password; reading it does not.
 */
export const RefreshRequest = z.object({
    refreshToken: z.string().optional(),
});
/**
 * ON WEB THE REFRESH TOKEN IS NOT IN THIS PAYLOAD — it is set as an HttpOnly,
 * Secure, SameSite cookie with reuse detection (TAD 4.6), so an XSS on the page
 * yields a credential that expires in fifteen minutes rather than one that
 * renews itself for weeks.
 *
 * A NATIVE session receives it in `refreshToken` instead, because there is no
 * browser to hold a cookie for it. That is a weaker position and is bought back
 * elsewhere: the token goes to the platform keystore, the family is bound to a
 * device, and the native lifetime is shorter than the web one. See ADR-0007.
 *
 * Both fields are optional so the WEB response is byte-identical to what it was
 * before native existed, and so this change is additive in the emitted OpenAPI.
 */
export const LoginResponse = z.object({
    accessToken: z.string(),
    /** Access token lifetime in seconds. TAD 4.6 fixes this at 15 minutes. */
    expiresIn: z.literal(900),
    /** NATIVE sessions only. Absent for web, where the cookie carries it. */
    refreshToken: z.string().optional(),
    /** Lifetime of `refreshToken` in seconds. Present whenever it is. */
    refreshExpiresIn: z.number().int().positive().optional(),
    user: z.object({
        id: Id,
        name: z.string(),
        /** The role CODE — stable, greppable, what the audit trail records. */
        role: RoleCode,
        /** The role's display name, which an administrator may have rewritten. */
        roleName: z.string(),
    }),
    /**
     * FR-01: field staff → outlet list; everyone else → dashboard.
     *
     * A HINT ABOUT WHERE TO LAND, NEVER A GATE. The staff app used to treat
     * `MANAGER_DASHBOARD` as "you are in the wrong application" and hold the
     * session behind an interstitial; it does not, because whether somebody may
     * start a visit is `visit:create` and nothing else, and the server answers
     * that on the endpoint. Derived from `visit:create` for the same reason.
     */
    landing: z.enum(['OUTLET_LIST', 'MANAGER_DASHBOARD']),
});
/**
 * The caller's own account — `GET /auth/profile`.
 *
 * Separate from `AccessScope` (`GET /auth/me`) on purpose. That object is a
 * security primitive: it is what every permission check and query predicate is
 * built from, and hanging a display name and a phone number off it would mean
 * every entitlement check carries PII it has no use for. This one is the
 * opposite — descriptive, read by exactly one screen, and safe to widen.
 *
 * Region NAMES, not the bare ids `AccessScope` carries. An id is what a
 * predicate needs; a person needs to read "Bandung Raya" and recognise their
 * own patch. There is no outlet count beside them: territory is granted by area
 * and nothing else, so "how many shops are mine" is answered by the area screen,
 * which counts them per region against the same predicate the list uses.
 *
 * `nik` is the login identifier and is always present. `email` and `phone` are
 * contact details and are independently nullable — the CHECK that used to
 * guarantee at least one of them went away with migration 0029, because an
 * account with neither can still sign in perfectly well.
 */
export const CurrentUserProfile = z.object({
    id: Id,
    name: z.string(),
    /** FR-01 — what this person types to sign in. */
    nik: z.string(),
    email: z.string().nullable(),
    /** Normalised to +62 form, as stored. */
    phone: z.string().nullable(),
    role: RoleCode,
    roleName: z.string(),
    /** True means unrestricted — `regions` is then empty and means nothing. */
    allRegions: z.boolean(),
    /**
     * `name` carries the COMPOSED LABEL — `900024 - BANDUNG`, not the bare area.
     * A region is a sub-distributor, and two of them routinely work areas with the
     * same name, so an entitlement list rendering bare areas is ambiguous exactly
     * where it matters. The server composes it; see `regionLabelExpr`.
     */
    regions: z.array(z.object({ id: Id, name: z.string() })),
    /** FR-01 makes 2FA optional, so this is status, not a guarantee. */
    totpEnrolled: z.boolean(),
    /**
     * Stamped at login, so on a live session this is when THIS session began —
     * which is the useful reading on a device passed between people.
     */
    lastLoginAt: z.string().nullable(),
    /**
     * The UU 27/2022 consent record, AS THE SERVER HOLDS IT.
     *
     * Nullable rather than optional: a user who has not consented has a null here,
     * which is a fact worth transmitting, not an absent field. Three flat fields
     * rather than a nested object because two of the three are what the profile
     * screen renders, and a nested null forces every caller to unwrap before it
     * can show a date.
     *
     * This is THE record. The copy in the handset's keystore is only the gate that
     * decides whether the app may collect location — see
     * `apps/staff-app/lib/consent.ts` for why the two are deliberately separate.
     */
    consentPolicyVersion: z.string().nullable(),
    consentDecision: ConsentDecision.nullable(),
    consentDecidedAt: z.string().nullable(),
});
/** Response when credentials are valid but a second factor is still required. */
export const TotpRequiredResponse = z.object({
    status: z.literal('TOTP_REQUIRED'),
    challengeId: Id,
});
/** Answer to a `TotpRequiredResponse` — `POST /auth/totp`. */
export const TotpChallengeRequest = z.object({
    challengeId: Id,
    code: z.string().regex(/^[0-9]{6}$/),
});
/** Confirms a `POST /auth/totp/enrol` by proving the secret was stored. */
export const EnrolTotpRequest = z.object({ code: z.string().regex(/^[0-9]{6}$/) });
/**
 * FR-14 user administration.
 *
 * `nik` is required because it is the login identifier; `email` and `phone` are
 * optional and stay optional, individually and jointly. An account with neither
 * is entirely usable now, which is the point of the change — most of the field
 * team have no work email.
 *
 * `password` has the same 6-character floor as `LoginRequest` — one policy
 * everywhere, so an account created here can always log in with the password it
 * was given.
 */
export const CreateUserRequest = z.object({
    name: z.string().min(1).max(200),
    nik: Nik,
    email: z.string().email().optional(),
    phone: z.string().regex(/^(\+62|0)8[1-9][0-9]{6,11}$/).optional(),
    password: z.string().min(6).max(256),
    /**
     * The role's id, not its code. Roles are administrable rows now (ADR-0006)
     * and a code may be rewritten between the moment a form loads its options and
     * the moment it posts; an id survives that.
     */
    roleId: Id,
    allRegions: z.boolean().default(false),
    regionIds: z.array(Id).default([]),
});
/**
 * Replaces a role's entire permission set — `PUT /auth/permissions`.
 *
 * Whole-set replace rather than add/remove, deliberately: the matrix is small
 * and a diff-based API would let two administrators each apply a valid delta and
 * produce a set neither intended.
 */
export const SetRolePermissionsRequest = z.object({
    roleId: Id,
    permissions: z.array(Permission),
});
/* -------------------------------------------------------------------------- */
/* Role administration — FR-13, ADR-0006                                       */
/* -------------------------------------------------------------------------- */
/**
 * A role as the administration screen sees it — `GET /auth/roles`.
 *
 * `system` marks a role the application depends on. Exactly one row carries it
 * (ADMIN): its code cannot be rewritten, it cannot be deleted, and it cannot be
 * stripped of `user:write`. That is a guard rail on top of the real invariant,
 * which the server enforces on every mutation: at least one active all-regions
 * user must hold `user:write`.
 *
 * `userCount` is here because it is the number an administrator needs before
 * pressing delete, and asking for it separately per role is N+1 requests to
 * render one table.
 */
export const RoleRow = z.object({
    id: Id,
    code: RoleCode,
    name: z.string(),
    description: z.string().nullable(),
    system: z.boolean(),
    active: z.boolean(),
    sortOrder: z.number().int(),
    userCount: z.number().int().nonnegative(),
});
/**
 * `POST /auth/roles`.
 *
 * Permissions are part of creation rather than a follow-up PUT: a role with an
 * empty permission set is invisible in every menu and grants nothing, so
 * shipping it as an intermediate state means the administrator's first attempt
 * at using the new role looks like a bug.
 */
export const CreateRoleRequest = z.object({
    code: RoleCode,
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    sortOrder: z.number().int().min(0).max(9999).default(100),
    permissions: z.array(Permission).default([]),
});
/**
 * `PATCH /auth/roles/:id`.
 *
 * `code` is here and is accepted for ordinary roles, but the server refuses it
 * for a `system` role. Renaming a code does NOT rewrite history: `actor_role`
 * on existing audit rows is a snapshot of what the role was called when the
 * event happened, which is the only reading that stays true.
 *
 * Permissions are not here — they are `PUT /auth/permissions`, whole-set, for
 * the reason given on `SetRolePermissionsRequest`.
 */
export const UpdateRoleRequest = z
    .object({
    code: RoleCode,
    name: z.string().min(1).max(120),
    description: z.string().max(500).nullable(),
    active: z.boolean(),
    sortOrder: z.number().int().min(0).max(9999),
})
    .partial();
/**
 * `DELETE /auth/roles/:id`.
 *
 * `reassignTo` is required when the role still has users, and the server says
 * how many when it refuses. Deleting a role is not a data-loss operation for
 * the users who held it — they move — but it does end their sessions, because
 * their access token still carries the deleted role's permissions for up to
 * fifteen minutes otherwise.
 */
export const DeleteRoleQuery = z.object({ reassignTo: Id.optional() });
/**
 * `GET /auth/permissions/catalogue`.
 *
 * The permission list is defined in code — every string here is compiled into a
 * `@RequirePermissions()` decorator, so one the server has never heard of would
 * guard nothing. What the database adds is presentation: a label and a group,
 * so the administration matrix stops carrying its own copy of both and stops
 * silently omitting permissions nobody remembered to add to it.
 */
export const PermissionCatalogueEntry = z.object({
    code: Permission,
    label: z.string(),
    groupLabel: z.string(),
    sortOrder: z.number().int(),
});
/**
 * Partial update of an existing user — `PATCH /auth/users/:id`.
 *
 * `nik`, `email` and `phone` are all editable here. They were not, on the
 * grounds that an identity change needs a deliberate flow rather than a text
 * field — but that argument only ever applied to email and phone BECAUSE they
 * were identifiers, and they are not any more. What is left is a correctable
 * data-entry field, and the NIK likewise: an employee number typed wrong at
 * creation must be fixable, and after migration 0029 every pre-existing account
 * is sitting on a `TMP…` placeholder that has to be replaced through this
 * endpoint.
 *
 * `email` and `phone` are NULLABLE here, and that is the difference between
 * "leave it alone" and "clear it": an absent key is untouched, an explicit null
 * erases the value.
 *
 * `password` is STILL not here, for two reasons that have nothing to do with
 * identity. The update handler audits `{ action: 'update', ...body }`, so a
 * password in this object would be written into `audit_events` in the clear.
 * And rotating a credential has to revoke the user's sessions, which editing a
 * name must not. It is `PUT /auth/users/:id/password` instead.
 *
 * `regionIds`, when present, REPLACES the existing entitlement rather than
 * adding to it — the same whole-set semantics as `SetRolePermissionsRequest`,
 * and for the same reason.
 */
export const UpdateUserRequest = z
    .object({
    name: z.string().min(1).max(200),
    nik: Nik,
    email: z.string().email().nullable(),
    phone: z.string().regex(/^(\+62|0)8[1-9][0-9]{6,11}$/).nullable(),
    roleId: Id,
    allRegions: z.boolean(),
    regionIds: z.array(Id),
})
    .partial();
/**
 * `PUT /auth/users/:id/password` — an administrator setting somebody's password.
 *
 * No current password, because the administrator does not know it and the whole
 * point of the endpoint is the case where nobody does. Authority comes from
 * `user:write` over a subject inside the caller's own regions, which is the same
 * gate that lets them deactivate the account outright.
 *
 * Succeeding here revokes every refresh token the subject holds. An
 * administrator resetting a password is usually reacting to a credential that
 * leaked, and leaving the leaked session alive for its full lifetime would make
 * the reset decorative.
 */
export const SetUserPasswordRequest = z.object({
    password: z.string().min(6).max(256),
});
/**
 * `POST /auth/password` — changing your OWN password.
 *
 * Both passwords carry the same 6-character floor `LoginRequest` uses:
 * `currentPassword` is only ever checked against what is already stored, and
 * `newPassword` must meet the one policy every other endpoint applies.
 *
 * Like the administrator's version, this ends every session — including the one
 * making the request. The client is expected to sign out and log back in; the
 * alternative is a session the server has already forgotten about.
 */
export const ChangePasswordRequest = z.object({
    currentPassword: z.string().min(6).max(256),
    newPassword: z.string().min(6).max(256),
});
/* -------------------------------------------------------------------------- */
/* Account deletion requests — Play policy, UU 27/2022 Art. 8                  */
/* -------------------------------------------------------------------------- */
/**
 * The lifecycle of a deletion request, and it is deliberately short.
 *
 * There is no CANCELLED and no IN_PROGRESS. A user who asked by mistake is
 * resolved by an administrator REJECTING with a reason, which leaves the same
 * two rows in the trail that a cancel would and needs no second self-service
 * write path — and a second self-service write path is the thing an attacker
 * would most like to find on a system that deactivates employee accounts.
 */
export const AccountDeletionStatus = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
/**
 * `POST /auth/account/deletion-request` — asking for your OWN account.
 *
 * `reason` is the requester's own words and is optional: Google Play requires
 * that asking be possible, not that it be justified. It is stored verbatim and
 * shown to the administrator, because "I am leaving the company" and "I think
 * someone else is using my login" need completely different handling and no
 * status enum can tell them apart.
 */
export const RequestAccountDeletionRequest = z.object({
    reason: z.string().trim().max(1000).optional(),
});
/**
 * What the requester gets back, and what `GET` on the same path returns.
 *
 * IDEMPOTENT BY CONTRACT: a second POST while one is pending returns the FIRST
 * request unchanged, with 200 and `created: false`. A 409 would be technically
 * defensible and practically wrong — a field user on a dropping connection who
 * taps twice has not made a mistake, and an error screen there reads as "the
 * request did not go through", which produces a third tap and a support call.
 */
export const AccountDeletionRequestStatus = z.object({
    id: Id,
    status: AccountDeletionStatus,
    requestedAt: z.string(),
    /** Present once an administrator has acted. */
    decidedAt: z.string().nullable(),
    /** The administrator's words on a rejection; null otherwise. */
    decisionReason: z.string().nullable(),
    /** False when this call found an existing pending request rather than making one. */
    created: z.boolean(),
});
/**
 * `POST /auth/deletion-requests/:id/decision` — the administrator's answer.
 *
 * A reason is REQUIRED on a rejection and optional on an approval, and the
 * asymmetry is the point: an approval explains itself, because the account is
 * gone. A rejection is a refusal the person is owed an explanation for, and it
 * is the text the support desk will end up reading back to them.
 */
export const DecideAccountDeletionRequest = z
    .object({
    decision: z.enum(['APPROVED', 'REJECTED']),
    reason: z.string().trim().max(1000).optional(),
})
    .refine((value) => value.decision !== 'REJECTED' || (value.reason?.length ?? 0) > 0, {
    message: 'alasan penolakan wajib diisi',
    path: ['reason'],
});
//# sourceMappingURL=auth.js.map